// @ts-strict-ignore
import { TFunction } from 'react-i18next'
import * as yup from 'yup'
import { useYupFields } from 'hooks/forms/useYupFields'
import { createBeneficiaryNameSchema } from 'utils/yupSchemas/createGenericNameSchema'
import { createPaymentReferenceSchema } from 'utils/yupSchemas/createReferenceSchema'
import {
  accountNumberConfirmValidation,
  accountNumberValidation,
  bankCodeValidation,
  validateAccountHolderName,
  validateAccountType,
  validateBankAddressField,
  validateBankName,
  validateBeneficiaryAddressField,
  validateIntermediaryAddressField,
  validateIntermediaryBankCode,
  validateIntermediaryBankName,
  validateIntermediarySwiftCode,
  validateSwiftCode,
} from './fiatBankAccountValidators'

export type ContactFiatInstrumentFormData = {
  // Currency details
  currency: string
  paymentMethods: string[]
  transferDestination: string
  // Basic beneficiary details
  entityType: string
  description: string
  isSelfOwned: boolean
  firstName?: string
  lastName?: string
  businessName?: string
  beneficiaryAddressLine1?: string
  beneficiaryAddressLine2?: string
  beneficiaryCity?: string
  beneficiaryRegion?: string
  beneficiaryPostCode?: string
  beneficiaryCountryCode?: string
  beneficiaryCountryName?: string
  sameAsContactAddress?: boolean
  sameAccountHolderNameAsContact?: boolean
  paymentReference?: string
  // Bank account details
  accountNumber: string
  accountNumberConfirm?: string // USD ACH and FEDWIRE
  bankCode: string
  swiftCode?: string // For GBP when both FPS and SWIFT are selected
  accountType?: string
  bankName?: string
  bankAddressLine1?: string
  bankAddressLine2?: string
  bankCity?: string
  bankRegion?: string
  bankPostCode?: string
  bankCountryCode?: string
  bankCountryName?: string
  bankNameCode?: string
  // Intermediary bank details
  isIntermediaryBankDetailsRequired?: boolean
  intermediaryBankCode?: string
  intermediaryBankSwiftCode?: string
  intermediaryBankName?: string
  intermediaryBankAddressLine1?: string
  intermediaryBankAddressLine2?: string
  intermediaryBankCity?: string
  intermediaryBankRegion?: string
  intermediaryBankPostCode?: string
  intermediaryBankCountryCode?: string
  // Auth
  twoFactorCode?: string
  challengeCode?: string
  callbackUrl?: string
}

export type ContactFiatInstrumentFormKeys = keyof ContactFiatInstrumentFormData

export const contactFiatInstrumentSchema = (t: TFunction<'translation', undefined>) => {
  const { getRequiredYupField, getCheckboxYupField } = useYupFields()

  return yup.object<Record<ContactFiatInstrumentFormKeys, yup.AnySchema>>({
    // Currency details
    currency: getRequiredYupField({
      label: t('formFields.currency'),
    }),
    paymentMethods: yup
      .array()
      .of(yup.string())
      .required(t('contacts.pleaseSelectAtLeastOnePaymentMethod'))
      .min(1, t('contacts.pleaseSelectAtLeastOnePaymentMethod')),
    transferDestination: yup.string().notRequired(),

    // Basic beneficiary details
    entityType: getRequiredYupField({
      label: '',
    }),
    description: createBeneficiaryNameSchema(t('formFields.beneficiaryDescription'), t).trim(),
    isSelfOwned: getCheckboxYupField(t('formFields.selfOwnedBankAccount')),
    firstName: yup
      .string()
      .trim()
      .label(t('firstName'))
      .test((value, ctx) => validateAccountHolderName(value, ctx, t, t('firstName'))),
    lastName: yup
      .string()
      .trim()
      .label(t('lastName'))
      .test((value, ctx) => validateAccountHolderName(value, ctx, t, t('lastName'))),
    businessName: yup
      .string()
      .trim()
      .label(t('businessName'))
      .test((value, ctx) => validateAccountHolderName(value, ctx, t, t('businessName'))),
    beneficiaryAddressLine1: yup
      .string()
      .trim()
      .label(t('addressLine1'))
      .test((value: string, ctx: yup.TestContext<ContactFiatInstrumentFormData>) =>
        validateBeneficiaryAddressField(value, ctx, t, t('addressLine1'))
      ),
    beneficiaryAddressLine2: yup.string().trim().optional().nullable(),
    beneficiaryCity: yup
      .string()
      .trim()
      .label(t('city'))
      .test((value: string, ctx: yup.TestContext<ContactFiatInstrumentFormData>) =>
        validateBeneficiaryAddressField(value, ctx, t, t('city'))
      ),
    beneficiaryRegion: yup
      .string()
      .trim()
      .label(t('region'))
      .test((value: string, ctx: yup.TestContext<ContactFiatInstrumentFormData>) =>
        validateBeneficiaryAddressField(value, ctx, t, t('region'))
      ),
    beneficiaryPostCode: yup
      .string()
      .trim()
      .label(t('postCode'))
      .test((value: string, ctx: yup.TestContext<ContactFiatInstrumentFormData>) =>
        validateBeneficiaryAddressField(value, ctx, t, t('postCode'))
      ),
    beneficiaryCountryCode: yup
      .string()
      .trim()
      .label(t('country'))
      .test((value: string, ctx: yup.TestContext<ContactFiatInstrumentFormData>) =>
        validateBeneficiaryAddressField(value, ctx, t, t('country'))
      ),
    beneficiaryCountryName: yup.string().trim().optional(),
    sameAsContactAddress: yup.boolean().default(false).optional(),
    sameAccountHolderNameAsContact: yup.boolean().default(false).optional(),
    paymentReference: createPaymentReferenceSchema(t('beneficiaryPaymentReference'), t),

    // Bank account details (same as before)
    accountNumber: yup
      .string()
      .trim()
      .test((value, ctx) => accountNumberValidation(value, ctx, t)),
    accountNumberConfirm: yup
      .string()
      .trim()
      .test((value, ctx) => accountNumberConfirmValidation(value, ctx, t)),
    bankCode: yup
      .string()
      .trim()
      .test((value, ctx) => bankCodeValidation(value, ctx, t)),
    swiftCode: yup
      .string()
      .trim()
      .test((value, ctx) => validateSwiftCode(value, ctx, t)),
    accountType: yup
      .string()
      .trim()
      .test((value, ctx) => validateAccountType(value, ctx, t)),
    bankName: yup
      .string()
      .trim()
      .test((value, ctx) => validateBankName(value, ctx, t, t('bankName'))),
    bankCountryName: yup.string().trim().notRequired(),
    bankAddressLine1: yup
      .string()
      .trim()
      .label(t('addressLine1'))
      .test((value, ctx: yup.TestContext<ContactFiatInstrumentFormData>) =>
        validateBankAddressField(value, ctx, t, t('addressLine1'))
      ),
    bankAddressLine2: yup.string().trim().nullable().optional(),
    bankCity: yup
      .string()
      .trim()
      .label(t('city'))
      .test((value, ctx: yup.TestContext<ContactFiatInstrumentFormData>) =>
        validateBankAddressField(value, ctx, t, t('city'))
      ),
    bankRegion: yup
      .string()
      .trim()
      .label(t('region'))
      .test((value, ctx: yup.TestContext<ContactFiatInstrumentFormData>) =>
        validateBankAddressField(value, ctx, t, t('region'))
      ),
    bankPostCode: yup
      .string()
      .trim()
      .label(t('postCode'))
      .test((value, ctx: yup.TestContext<ContactFiatInstrumentFormData>) =>
        validateBankAddressField(value, ctx, t, t('postCode'))
      ),
    bankCountryCode: yup
      .string()
      .trim()
      .label(t('country'))
      .test((value, ctx: yup.TestContext<ContactFiatInstrumentFormData>) =>
        validateBankAddressField(value, ctx, t, t('country'))
      ),
    bankNameCode: yup
      .string()
      .trim()
      .test((value, ctx) => validateBankName(value, ctx, t, t('bankName'))),

    // Intermediary bank details (same as before)
    isIntermediaryBankDetailsRequired: yup.boolean().default(false).optional(),
    intermediaryBankCode: yup
      .string()
      .trim()
      .test((value, ctx: yup.TestContext<ContactFiatInstrumentFormData>) =>
        validateIntermediaryBankCode(value, ctx, t)
      ),
    intermediaryBankSwiftCode: yup
      .string()
      .trim()
      .test((value, ctx: yup.TestContext<ContactFiatInstrumentFormData>) =>
        validateIntermediarySwiftCode(value, ctx, t)
      ),
    intermediaryBankName: yup
      .string()
      .trim()
      .test((value, ctx: yup.TestContext<ContactFiatInstrumentFormData>) =>
        validateIntermediaryBankName(value, ctx, t, t('bankName'))
      ),
    intermediaryBankAddressLine1: yup
      .string()
      .trim()
      .label(t('addressLine1'))
      .test((value, ctx: yup.TestContext<ContactFiatInstrumentFormData>) =>
        validateIntermediaryAddressField(value, ctx, t, t('addressLine1'))
      ),
    intermediaryBankAddressLine2: yup.string().trim().optional().nullable(),
    intermediaryBankCity: yup
      .string()
      .trim()
      .label(t('city'))
      .test((value, ctx: yup.TestContext<ContactFiatInstrumentFormData>) =>
        validateIntermediaryAddressField(value, ctx, t, t('city'))
      ),
    intermediaryBankRegion: yup
      .string()
      .trim()
      .label(t('region'))
      .test((value, ctx: yup.TestContext<ContactFiatInstrumentFormData>) =>
        validateIntermediaryAddressField(value, ctx, t, t('region'))
      ),
    intermediaryBankPostCode: yup
      .string()
      .trim()
      .label(t('postCode'))
      .test((value, ctx: yup.TestContext<ContactFiatInstrumentFormData>) =>
        validateIntermediaryAddressField(value, ctx, t, t('postCode'))
      ),
    intermediaryBankCountryCode: yup
      .string()
      .trim()
      .label(t('country'))
      .test((value, ctx: yup.TestContext<ContactFiatInstrumentFormData>) =>
        validateIntermediaryAddressField(value, ctx, t, t('country'))
      ),

    // Auth fields (same as before)
    twoFactorCode: yup.string().notRequired(),
    challengeCode: yup.string().notRequired(),
    callbackUrl: yup.string().notRequired(),
  })
}
